import {
  Plain as PlainString,
  Config as StringConfig,
  PlainResult as StringPlainResult,
  Result as StringResult,
  transform as transformString
} from './string'
import {
  Plain as PlainNumber,
  Config as NumberConfig,
  PlainResult as NumberPlainResult,
  Result as NumberResult,
  transform as transformNumber
} from './number'
import {
  Plain as PlainObject,
  Config as ObjectConfig,
  PlainResult as ObjectPlainResult,
  Result as ObjectResult,
  transform as transformObject
} from './object'
import {
  Plain as PlainEnum,
  Config as EnumConfig,
  PlainResult as EnumPlainResult,
  Result as EnumResult,
  transform as transformEnum
} from './enum'
import {
  Plain as PlainBoolean,
  Config as BooleanConfig,
  PlainResult as BooleanPlainResult,
  Result as BooleanResult,
  transform as transformBoolean
} from './boolean'
import {
  Plain as PlainDate,
  Config as DateConfig,
  PlainResult as DatePlainResult,
  Result as DateResult,
  transform as transformDate
} from './date'
import { Config as SchemaConfig, Result as SchemaResult, transform as transformSchema } from './schema'

export interface BaseConfig {
  array?: boolean
}

export type Nullable<T extends { nullable?: boolean }, V> = T['nullable'] extends true ? V | null : V

type Plain = PlainString | PlainNumber | PlainBoolean | PlainObject | PlainEnum | PlainDate
type Config = StringConfig | NumberConfig | BooleanConfig | ObjectConfig | EnumConfig | DateConfig | SchemaConfig

type SchemaType = Plain | Config
export interface Schema {
  [x: string]: SchemaType
}

type PlainResult<T> = T extends 'string'
  ? StringPlainResult
  : T extends 'number'
  ? NumberPlainResult
  : T extends 'boolean'
  ? BooleanPlainResult
  : T extends 'date'
  ? DatePlainResult
  : T extends 'object'
  ? ObjectPlainResult
  : EnumPlainResult<T>

type WithConfigResult<T extends Config> = T extends StringConfig
  ? StringResult<T>
  : T extends NumberConfig
  ? NumberResult<T>
  : T extends BooleanConfig
  ? BooleanResult
  : T extends DateConfig
  ? DateResult<T>
  : T extends ObjectConfig
  ? ObjectResult<T>
  : T extends SchemaConfig
  ? SchemaResult<T>
  : T extends EnumConfig
  ? EnumResult<T>
  : unknown

export type Result<T extends Schema> = {
  [P in keyof T]: T[P] extends Config ? WithConfigResult<T[P]> : PlainResult<T[P]>
}

export function isString(type: SchemaType): type is PlainString | StringConfig {
  return type === 'string' || (typeof type === 'object' && 'type' in type && type.type === 'string')
}
export function isNumber(type: SchemaType): type is PlainNumber | NumberConfig {
  return type === 'number' || (typeof type === 'object' && 'type' in type && type.type === 'number')
}
export function isBoolean(type: SchemaType): type is PlainBoolean | BooleanConfig {
  return type === 'boolean' || (typeof type === 'object' && 'type' in type && type.type === 'boolean')
}
function isDate(type: SchemaType): type is PlainDate | DateConfig {
  return type === 'date' || (typeof type === 'object' && 'type' in type && type.type === 'date')
}
function isObject(type: SchemaType): type is PlainObject | ObjectConfig {
  return type === 'object' || (typeof type === 'object' && 'type' in type && type.type === 'object')
}
function isSchema(type: SchemaType): type is SchemaConfig {
  return typeof type === 'object' && 'type' in type && type.type === 'schema'
}
function isEnum(type: SchemaType): type is PlainEnum | EnumConfig {
  return typeof type === 'object' && (!('type' in type) || type.type === 'enum')
}
export type FormType<S extends Schema, R = Result<S>> = { [P in keyof R]: R[P] }
export type RemoveNulls<ObjectType, KeysType extends keyof ObjectType = keyof ObjectType, Rest = Omit<ObjectType, KeysType>> = {
  [K in KeysType]: NonNullable<ObjectType[K]>
} & Rest
export type ReplaceWithValidated<ObjectType, Validated> = {
  [P in keyof ObjectType]: P extends keyof Validated ? Validated[P] : ObjectType[P]
}

const getInitialValues = <O extends { [P in keyof S]?: any }, S extends Schema>(object: O, schema: S): FormType<S> =>
  Object.keys(schema).reduce<any>((acc, cur) => {
    const type = schema[cur]

    if (isString(type)) {
      return { ...acc, [cur]: transformString(object[cur], type) }
    } else if (isNumber(type)) {
      return { ...acc, [cur]: transformNumber(object[cur], type) }
    } else if (isBoolean(type)) {
      return { ...acc, [cur]: transformBoolean(object[cur], type) }
    } else if (isDate(type)) {
      return { ...acc, [cur]: transformDate(object[cur], type) }
    } else if (isObject(type)) {
      return { ...acc, [cur]: transformObject(object[cur], type) }
    } else if (isSchema(type)) {
      return { ...acc, [cur]: transformSchema(object[cur], type) }
    } else if (isEnum(type)) {
      return { ...acc, [cur]: transformEnum(object[cur], type) }
    }

    return { ...acc, [cur]: undefined as unknown }
  }, {})

export default getInitialValues
